/**
 * Turns the newest-first message list the shared contexts keep into a
 * chronological render list with day separators inserted.
 */
import type { ExtendedChatMessage } from '@wlb/shared/types/chat';

export type ChatTimelineItem =
  | { type: 'day'; key: string; label: string }
  | { type: 'message'; key: string; message: ExtendedChatMessage };

function toDate(value: Date | number): Date {
  return typeof value === 'number' ? new Date(value) : value;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

function formatDayLabel(date: Date, t: (key: string) => string): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(date, today)) return t('chat.today');
  if (isSameDay(date, yesterday)) return t('chat.yesterday');
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function buildChatTimeline(
  messages: ExtendedChatMessage[],
  t: (key: string) => string,
): ChatTimelineItem[] {
  const items: ChatTimelineItem[] = [];
  let lastDate: Date | null = null;

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const date = toDate(message.createdAt);
    if (!lastDate || !isSameDay(lastDate, date)) {
      items.push({
        type: 'day',
        key: `day-${date.toDateString()}`,
        label: formatDayLabel(date, t),
      });
      lastDate = date;
    }
    items.push({ type: 'message', key: String(message._id), message });
  }

  return items;
}
